import { useMemo, useState, useCallback, useRef } from "react";
import type { Editor, JSONContent } from "@tiptap/react";
import type { ContentField } from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";
import { parseFieldDef } from "@/lib/parseFieldDef";

export const TOC_SELECTOR = ".section-heading-node, .sub-template-header";

interface TocEntry {
  text: string;
  sectionId?: string;
  isSubTemplate: boolean;
  level: number;
}

function sectionToTocEntry(node: JSONContent): TocEntry {
  const text = (node.content ?? []).map((n: JSONContent) => n.text ?? "").join("");
  return {
    isSubTemplate: false,
    level: 0,
    sectionId: node.attrs?.sectionId as string | undefined,
    text,
  };
}

function subTemplateToTocEntries(
  field: ContentField & { type: "sub-template" },
  subData: SubTemplateData | undefined,
): TocEntry[] {
  const entries: TocEntry[] = [
    { isSubTemplate: true, level: 0, text: subData?.templateName ?? field.label },
  ];
  for (const subNode of subData?.content ?? []) {
    if (subNode.type === "section") {
      entries.push({ isSubTemplate: true, level: 1, sectionId: undefined, text: subNode.title });
    }
  }
  return entries;
}

function buildHeadingList(
  content: JSONContent[] | undefined,
  subTemplateValues: Record<string, SubTemplateData>,
): TocEntry[] {
  const list: TocEntry[] = [];
  for (const node of content ?? []) {
    if (node.type === "section") {
      list.push(sectionToTocEntry(node));
    } else if (node.type === "fieldNode") {
      const field = parseFieldDef((node.attrs?.fieldDef as string | undefined) ?? "");
      if (field?.type === "sub-template") {
        list.push(...subTemplateToTocEntries(field, subTemplateValues[field.id]));
      }
    }
  }
  return list;
}

function useScrollToHeading(
  editorRef: React.RefObject<Editor | null>,
  editorWrapperEl: HTMLDivElement | null,
) {
  return useCallback(
    (headingIndex: number) => {
      const editor = editorRef.current;
      const wrapper = editorWrapperEl;
      if (!editor || !wrapper) {
        return;
      }
      const headings = [...editor.view.dom.querySelectorAll(TOC_SELECTOR)] as HTMLElement[];
      const el = headings[headingIndex];
      if (!el) {
        return;
      }
      try {
        const targetTop =
          el.getBoundingClientRect().top - wrapper.getBoundingClientRect().top + wrapper.scrollTop;
        wrapper.scrollTop = targetTop;
      } catch {
        /* ignore */
      }
    },
    [editorRef, editorWrapperEl],
  );
}

function useTocPopoverState() {
  const [activeHeadingIndex, setActiveHeadingIndex] = useState(0);
  const [tocPopoverOpen, setTocPopoverOpen] = useState(false);
  const tocBtnRef = useRef<HTMLButtonElement>(null);
  const [tocPopoverPos, setTocPopoverPos] = useState<{ bottom: number; left: number } | null>(null);
  return {
    activeHeadingIndex,
    setActiveHeadingIndex,
    setTocPopoverOpen,
    setTocPopoverPos,
    tocBtnRef,
    tocPopoverOpen,
    tocPopoverPos,
  };
}

export function useTocNavigation(
  unifiedDocument: JSONContent | null,
  subTemplateValues: Record<string, SubTemplateData>,
  sectionToggles: Record<string, boolean>,
  editorRef: React.RefObject<Editor | null>,
  editorWrapperEl: HTMLDivElement | null,
  scrollInitiatorRef: React.RefObject<"tiptap" | "cm" | "toc" | null>,
  scrollInitiatorTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
) {
  const popover = useTocPopoverState();

  const allHeadings = useMemo(
    () => buildHeadingList(unifiedDocument?.content, subTemplateValues),
    [unifiedDocument, subTemplateValues],
  );

  const tocEntries = useMemo(
    () =>
      allHeadings.map(({ isSubTemplate, level, sectionId, text }, index) => ({
        index,
        isDisabled: sectionId ? sectionToggles[sectionId] === false : false,
        isSubTemplate,
        level,
        text,
      })),
    [allHeadings, sectionToggles],
  );

  const scrollToHeading = useScrollToHeading(editorRef, editorWrapperEl);

  const handleTocNavigate = useCallback(
    (idx: number) => {
      popover.setTocPopoverOpen(false);
      scrollInitiatorRef.current = "toc";
      clearTimeout(scrollInitiatorTimerRef.current);
      scrollInitiatorTimerRef.current = setTimeout(() => {
        scrollInitiatorRef.current = null;
      }, 300);
      popover.setActiveHeadingIndex(idx);
      scrollToHeading(idx);
    },
    [scrollToHeading, scrollInitiatorRef, scrollInitiatorTimerRef, popover],
  );

  return { allHeadings, handleTocNavigate, scrollToHeading, tocEntries, ...popover };
}
